// components/crm/dialogs/ImportLeadsDialog.jsx
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../lib/apiError';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Download a sample .xlsx template ─────────────────────────────────────────
function downloadTemplate() {
  // Build a minimal CSV that looks like the expected Excel columns
  const header = 'name,phone,email,course_id,batch_id';
  const sample = 'John Doe,9876543210,john@example.com,,';
  const blob = new Blob([`${header}\n${sample}\n`], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = 'leads_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function ImportLeadsDialog({ open, onOpenChange, onSuccess }) {
  const [file,       setFile]       = useState(null);
  const [dragging,   setDragging]   = useState(false);
  const [importing,  setImporting]  = useState(false);
  const [result,     setResult]     = useState(null); // { created, skipped, skippedDetails }
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setImporting(false);
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onOpenChange(false);
  };

  const handleFileSelect = (selected) => {
    if (!selected) return;
    const ext = selected.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Please upload an .xlsx, .xls or .csv file');
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5 MB');
      return;
    }
    setFile(selected);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const token = localStorage.getItem('admin_token');
      const fd = new FormData();
      fd.append('file', file);

      const res = await fetch(`${API_BASE_URL}/leads/import`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    fd,
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || 'Import failed');
        return;
      }

      setResult({
        created:        data.created        || 0,
        skipped:        data.skipped        || 0,
        skippedDetails: data.skippedDetails || [],
      });

      if (data.created > 0) {
        toast.success(data.message);
        onSuccess?.();
      } else {
        toast.warning('No leads were imported. Check the skipped rows below.');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Import failed'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        {/* ── Header ── */}
        <DialogHeader className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-violet-600 to-purple-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-white/20">
                <ArrowUpTrayIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-white">Import Leads</DialogTitle>
                <p className="text-xs text-violet-200 mt-0.5">Upload an Excel or CSV file to bulk-add leads</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={importing}
              className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-all disabled:opacity-40"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </DialogHeader>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto popup-scroll p-6 space-y-5">

          {/* Column spec info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Expected Columns</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-blue-700">
              <span className="font-mono bg-blue-100 rounded px-1.5 py-0.5">name <span className="text-red-500">*</span></span>
              <span className="font-mono bg-blue-100 rounded px-1.5 py-0.5">phone <span className="text-red-500">*</span></span>
              <span className="font-mono bg-blue-100 rounded px-1.5 py-0.5">email <span className="text-gray-400">(optional)</span></span>
              <span className="font-mono bg-blue-100 rounded px-1.5 py-0.5">course_id <span className="text-gray-400">(optional)</span></span>
              <span className="font-mono bg-blue-100 rounded px-1.5 py-0.5">batch_id <span className="text-gray-400">(optional)</span></span>
            </div>
            <p className="text-[11px] text-blue-600">
              <span className="text-red-500">*</span> <strong>name</strong> and <strong>phone</strong> are required.
              Rows with a duplicate phone number are automatically skipped. No emails are sent for imported leads.
            </p>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-1.5 text-xs text-violet-700 hover:text-violet-900 font-medium transition-colors"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              Download sample template
            </button>
          </div>

          {/* Drop zone */}
          {!result && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => !importing && inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                ${importing
                  ? 'border-violet-400 bg-violet-50 cursor-not-allowed'
                  : dragging
                    ? 'border-violet-500 bg-violet-50 scale-[1.01]'
                    : file
                      ? 'border-emerald-400 bg-emerald-50'
                      : 'border-gray-300 hover:border-violet-400 hover:bg-violet-50/50'
                }`}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                disabled={importing}
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />

              {importing ? (
                <div>
                  <svg className="h-10 w-10 mx-auto text-violet-500 mb-2 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <p className="text-sm font-medium text-violet-700">Uploading & processing…</p>
                </div>
              ) : file ? (
                <div className="flex flex-col items-center gap-2">
                  <DocumentTextIcon className="h-10 w-10 text-emerald-500" />
                  <p className="text-sm font-semibold text-emerald-700">{file.name}</p>
                  <p className="text-xs text-emerald-500">
                    {(file.size / 1024).toFixed(1)} KB · Click to change file
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <ArrowUpTrayIcon className="h-10 w-10 text-gray-400" />
                  <p className="text-sm font-medium text-gray-700">Click to upload or drag & drop</p>
                  <p className="text-xs text-gray-400">.xlsx, .xls or .csv · max 5 MB</p>
                </div>
              )}
            </div>
          )}

          {/* Result summary */}
          {result && (
            <div className="space-y-3">
              {/* Success banner */}
              {result.created > 0 && (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <CheckCircleIcon className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">
                      {result.created} lead{result.created !== 1 ? 's' : ''} imported successfully
                    </p>
                    {result.skipped > 0 && (
                      <p className="text-xs text-emerald-600 mt-0.5">
                        {result.skipped} row{result.skipped !== 1 ? 's' : ''} were skipped — see details below
                      </p>
                    )}
                  </div>
                </div>
              )}
              {result.created === 0 && (
                <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 flex-shrink-0" />
                  <p className="text-sm font-semibold text-amber-800">No leads were imported</p>
                </div>
              )}

              {/* Skipped rows */}
              {result.skippedDetails?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Skipped rows</p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto popup-scroll">
                    {result.skippedDetails.map((s, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <span className="font-medium text-red-700">{s.row?.name || `Row ${i + 1}`}</span>
                          <span className="text-red-500 ml-1">— {s.reason}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Import another */}
              <button
                onClick={reset}
                className="text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
              >
                ↑ Import another file
              </button>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={importing}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleImport}
              disabled={!file || importing}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
            >
              {importing ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Importing…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ArrowUpTrayIcon className="h-4 w-4" />
                  Import Leads
                </span>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
